import type { Family } from "@/lib/family";

export interface RevisionUsed {
  revision_no: number;
  revision_id: string;
}

export interface PredictResult {
  status: number;
  latency_ms: number;
  body: unknown;
  headers: Record<string, string>;
}

function urlFor(modelId: string, family: Family): string {
  if (family === "forecast") return `/inference/v1/models/${modelId}/forecast`;
  return `/inference/v1/models/${modelId}/${family}/predict`;
}

export async function callPredict(
  modelId: string,
  family: Family,
  body: Record<string, unknown>,
  secret: string,
): Promise<PredictResult> {
  const start = performance.now();
  // Inference endpoints use bearer auth (the project API key), not the shell
  // session cookie — so we go via fetch() directly with the openapi-fetch base
  // URL. The typed client doesn't help for these specific endpoints because
  // their auth model is different from the rest of the surface.
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (secret) headers.Authorization = `Bearer ${secret}`;

  const base = typeof window !== "undefined" ? window.location.origin : "";
  const resp = await fetch(`${base}${urlFor(modelId, family)}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  const latency_ms = Math.round(performance.now() - start);

  const respHeaders: Record<string, string> = {};
  resp.headers.forEach((v, k) => {
    respHeaders[k] = v;
  });

  let responseBody: unknown;
  try {
    responseBody = await resp.json();
  } catch {
    responseBody = null;
  }

  // Predict endpoints use bearer auth (not the cookie-based session auth that
  // typedApi is configured for), so they stay on raw fetch.
  return { status: resp.status, latency_ms, body: responseBody, headers: respHeaders };
}
